import { useAppStore } from "@hooks/useAppStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import {
	getAnsweredCount,
	getFallbackProficiencyLevelLabel,
} from "@memorang/helpers/language-learning";
import {
	SKILL_MASTERY_WINDOW,
	resolveMasteryForTagId,
} from "@memorang/helpers/language-learning-mastery";
import { isProficiencyTag, isSkillTag } from "@memorang/helpers/tag";
import type { MixedItemTag } from "@memorang/types";
import type { ConnectedLevel } from "@memorang/ui/components/ConnectedLevels";
import { toConnectedLevels } from "@memorang/ui/components/ConnectedLevels/helpers";
import type { ItemTypeStat } from "@memorang/ui/components/ItemTypeDetailModal/types";
import {
	buildProficiencyIconMap,
	getProficiencyLevelIcons,
	getSkillIcon,
} from "@memorang/ui/constants";
import type { LevelIconName } from "@memorang/ui/icon";
import { useTranslation } from "react-i18next";
import { useParsedRecommendationContext } from "./useParsedRecommendationContext";

export type SkillDetail = {
	skill: MixedItemTag;
	icon: LevelIconName;
	title: string;
	description?: string;
	levels: ConnectedLevel[];
	stats: ItemTypeStat[];
	levelKnown: boolean;
};

export const useSkillDetail = (skillId?: string): SkillDetail | undefined => {
	const { data: commonDistribution } = useMixedItemDist();
	const recommendationContext = useParsedRecommendationContext();
	const proficiencyLevelsConfig = useAppStore(
		(state) => state.app.proficiencyLevels,
	);
	const hideTotalItemCounts = useAppStore(
		(state) => state.app.hideTotalItemCounts,
	);
	const showProficiencyEstimates = useAppStore(
		(state) => state.reportView?.options?.showProficiencyEstimates,
	);
	const { t } = useTranslation();

	const tags = commonDistribution?.tags ?? [];
	const skill = tags
		.filter(isSkillTag)
		.find((tag) => String(tag.id) === skillId);
	if (!skill) {
		return undefined;
	}

	const proficiencyTags = tags.filter(isProficiencyTag);
	const levelIcons = getProficiencyLevelIcons(proficiencyLevelsConfig);
	const { fills, estimatedLevel, displayLevel } = resolveMasteryForTagId(
		recommendationContext,
		skill.id,
		SKILL_MASTERY_WINDOW,
		getFallbackProficiencyLevelLabel(proficiencyTags),
	);
	const answered = getAnsweredCount(skill);
	const total = skill.numItems ?? (skill.items ?? []).length;
	const responses = skill.totalResponses;
	const answeredStat: ItemTypeStat = hideTotalItemCounts
		? {
				icon: "check-circle-outline",
				value: String(responses),
				label: t("dashboard.itemTypes.responsesLabel", { count: responses }),
			}
		: {
				icon: "check-circle-outline",
				value:
					total > 0 && answered === total
						? t("dashboard.itemTypes.answeredAll")
						: t("dashboard.itemTypes.answeredPartial", { answered, total }),
				label: t("dashboard.itemTypes.answeredLabel_other"),
			};

	const stats: ItemTypeStat[] = [
		{
			icon:
				(displayLevel
					? buildProficiencyIconMap(proficiencyLevelsConfig)[displayLevel]
					: undefined) ?? "school-outline",
			value: displayLevel ?? "",
			label: t("dashboard.itemTypes.detail.estimatedLevelLabel"),
		},
		answeredStat,
	];

	return {
		skill,
		icon: getSkillIcon(skill.title),
		title: skill.title,
		description: skill.description,
		levels: showProficiencyEstimates
			? toConnectedLevels(fills, levelIcons)
			: [],
		stats,
		levelKnown: Boolean(estimatedLevel),
	};
};
